import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Camera, Copy, ExternalLink, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import PublicShell from "@/components/PublicShell";
import CreatorKit from "@/components/CreatorKit";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { creatorApi, creatorToken, setCreatorToken, money, cardCls, inputCls, btnGold } from "@/lib/creators";

function CopyLink({ label, url, testId }) {
  return (
    <div className="bg-[#0B0F19] border border-[#D4AF37]/20 rounded-xl p-3">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[#A7B0C0]">{label}</p>
      <div className="flex items-center gap-2 mt-1">
        <span data-testid={testId} className="flex-1 min-w-0 truncate gold-text">{url.replace("https://", "")}</span>
        <button data-testid={`${testId}-copy`} aria-label={`Copy ${label}`} onClick={() => navigator.clipboard.writeText(url).then(() => toast.success("Link copied"))} className="gold-text"><Copy size={16} /></button>
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return <div className={`${cardCls} p-4`}><p className="text-[11px] uppercase tracking-[0.2em] text-[#A7B0C0]">{label}</p><p data-testid={testId} className="text-2xl font-light gold-text mt-1">{value}</p></div>;
}

function Profile({ c, onSaved }) {
  const [name, setName] = useState(c.displayName);
  const [bio, setBio] = useState(c.bio || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await creatorApi("patch", "/creators/me", { displayName: name, bio }); toast.success("Profile saved"); onSaved(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Photo must be under 5 MB");
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { await creatorApi("post", "/creators/me/avatar", fd); toast.success("Photo updated"); onSaved(); } catch (e2) { toast.error(formatApiErrorDetail(e2.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <section className={cardCls} data-testid="creator-profile">
      <h2 className="text-xl mb-4">Your page</h2>
      <div className="flex items-center gap-4 mb-4">
        {c.avatarUrl ? <img src={`${process.env.REACT_APP_BACKEND_URL}${c.avatarUrl}`} alt="" className="w-16 h-16 rounded-full object-cover border border-[#D4AF37]/50" />
          : <span className="w-16 h-16 rounded-full bg-[#D4AF37]/15 flex items-center justify-center text-2xl gold-text">{c.displayName[0]}</span>}
        <label className="flex items-center gap-2 text-sm gold-text cursor-pointer" data-testid="creator-avatar-label">
          <Camera size={16} /> Upload photo
          <input data-testid="creator-avatar-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" />
        </label>
      </div>
      <input data-testid="creator-name-input" value={name} onChange={(e) => setName(e.target.value)} maxLength={50} className={`${inputCls} mb-3`} />
      <textarea data-testid="creator-bio-input" value={bio} onChange={(e) => setBio(e.target.value)} maxLength={400} rows={3} placeholder="A short bio shown on your page" className={inputCls} />
      <button data-testid="creator-profile-save" onClick={save} disabled={busy} className={`${btnGold} mt-3 px-6 py-2.5`}>{busy ? <Loader2 size={16} className="animate-spin" /> : "Save"}</button>
    </section>
  );
}

function Payouts({ c, onSaved }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try { const { data } = await creatorApi("post", "/creators/me/stripe-connect", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };
  const [params] = useSearchParams();
  useEffect(() => { if (params.get("stripe") === "done") creatorApi("post", "/creators/me/stripe-refresh").then(onSaved).catch(() => {}); }, [params, onSaved]);
  return (
    <section className={cardCls} data-testid="creator-payouts">
      <h2 className="text-xl mb-1">Payout settings</h2>
      <p className="text-sm text-[#A7B0C0] mb-4">Commission is paid monthly into your bank through Stripe. Approved earnings are sent at the start of each month.</p>
      {c.payoutsEnabled ? <p data-testid="payouts-status" className="text-emerald-300 text-sm">Payouts are set up. You're ready to get paid.</p> : (
        <button data-testid="payouts-connect" onClick={connect} disabled={busy || c.status !== "approved"} className={`${btnGold} px-6 py-2.5 inline-flex items-center gap-2`}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ExternalLink size={16} />} {c.stripeConnected ? "Finish payout setup" : "Set up payouts with Stripe"}
        </button>
      )}
      {c.status !== "approved" && <p className="text-xs text-[#A7B0C0] mt-2">Available once your application is approved.</p>}
    </section>
  );
}

export default function CreatorDashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [out, setOut] = useState(!creatorToken());
  const load = useCallback(() => creatorApi("get", "/creators/me").then(({ data }) => setD(data)).catch(() => { setCreatorToken(""); setOut(true); }), []);
  useEffect(() => { if (!out) load(); }, [load, out]);
  if (out) return <Navigate to="/creators/login" replace />;
  if (!d) return <PublicShell testId="creator-dashboard-loading"><Loader2 className="animate-spin gold-text mx-auto my-32" size={28} /></PublicShell>;
  const { creator: c, links, stats } = d;
  const cur = stats.currency;
  const logout = async () => { await api.post("/creators/logout").catch(() => {}); setCreatorToken(""); navigate("/creators"); };
  return (
    <PublicShell testId="creator-dashboard">
      <div className="px-5 pt-4 lg:max-w-5xl lg:mx-auto lg:px-10 space-y-5 fade-up">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] gold-text">Creator dashboard</p>
            <h1 className="text-3xl font-light">Hi, {c.displayName}</h1>
            <p data-testid="creator-status" className="text-sm text-[#A7B0C0] mt-1">
              {c.status === "approved" ? `Earning ${Math.round(c.commissionRate * 100)}% commission` : "Your application is being reviewed. Your links go live once approved."}
            </p>
          </div>
          <button data-testid="creator-logout" onClick={logout} className="flex items-center gap-1 text-sm text-[#A7B0C0] hover:text-starlight"><LogOut size={15} /> Sign out</button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <CopyLink label="Your page" url={links.page} testId="creator-link-page" />
          <CopyLink label="Referral link" url={links.ref} testId="creator-link-ref" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Stat label="Clicks · 30 days" value={stats.clicks30d} testId="stat-clicks" />
          <Stat label="Sales" value={stats.conversions} testId="stat-conversions" />
          <Stat label="Pending" value={money(stats.earnings.pending, cur)} testId="stat-pending" />
          <Stat label="Approved" value={money(stats.earnings.approved, cur)} testId="stat-approved" />
          <Stat label="Paid" value={money(stats.earnings.paid, cur)} testId="stat-paid" />
        </div>
        {d.conversions.length > 0 && (
          <section className={cardCls} data-testid="creator-conversions">
            <h2 className="text-xl mb-3">Recent sales</h2>
            <ul className="divide-y divide-[#D4AF37]/10">
              {d.conversions.map((x, i) => (
                <li key={i} className="flex items-center justify-between py-2 text-sm">
                  <span>{x.productType.replace(/_/g, " ")} · <span className="text-[#A7B0C0]">{new Date(x.createdAt).toLocaleDateString()}</span></span>
                  <span className="gold-text">{money(x.commissionAmount, x.currency)} <span className="text-xs text-[#A7B0C0]">{x.status}</span></span>
                </li>
              ))}
            </ul>
          </section>
        )}
        <div className="grid gap-5 lg:grid-cols-2">
          <Profile c={c} onSaved={load} />
          <Payouts c={c} onSaved={load} />
        </div>
        <CreatorKit creator={c} links={links} />
      </div>
    </PublicShell>
  );
}
